import React, { useState,useEffect } from 'react';
import { Button, Paper, Dialog, DialogActions, DialogContent, DialogTitle,TextField, Tooltip,OutlinedInput, Grid, Typography, Box, InputLabel, FormControl, MenuItem, Select, CircularProgress,ListItemText,Checkbox ,FormControlLabel,Radio,RadioGroup,Stack,Chip} from '@mui/material';
import { ButtonSpinner, ButtonSpinnerSlot } from '../ui/ButtonSpinner';
import { Btn } from '../ui/primitives';
// Prompt text is no longer built in the frontend: every prompt this dialog shows or
// runs is assembled server-side from the DB templates (Prompts -> "4. Manually
// generated questions"), so editing them there takes effect here immediately.
import PromptDialog from './promptdialog';
import { GenerateQuestions, GenerateQuestionsFromSpec, PreviewYakiPrompt } from './generatequestions';
import QuestionDialog from './questiondialog';
import QuestionsShowdialog from './showquestion';
import styled from 'styled-components';
import FieldHelp from '../ui/FieldHelp';
import { GENERATE_QUESTION_FIELD_HELP as HELP } from './generateQuestionFieldHelp';
import EditNoteIcon from '@mui/icons-material/EditNote';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import axios from "axios";
import useDemoActionGate from '../hooks/useDemoActionGate';
import DemoLockedDialog from '../ui/DemoLockedDialog';
import ClearIcon from '@mui/icons-material/Clear';
import PassageDialog from "./PassageDialog"
import CloseIcon from '@mui/icons-material/Close';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';

const Container = styled.div`
  padding: 0;
`;

const ModalFoot = styled.div`
  width: 100%;
  padding: 16px 24px;
  border-top: 1px solid ${({ theme }) => theme.border};
  display: flex;
  gap: 10px;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  background: ${({ theme }) => theme.surface2};
`;

const ModalFootActions = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
  margin-left: auto;
`;

const LoadingPopupWrapper = styled.div`
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background-color: ${({ theme }) => theme.surface};
    border: 1px solid ${({ theme }) => theme.border};
    border-radius: ${({ theme }) => theme.rLg};
    box-shadow: ${({ theme }) => theme.shadowLg};
    width: min(440px, calc(100vw - 32px));
    z-index: 1000;
    overflow: hidden;
`;

const RunHeader = styled.div`
    padding: 18px 20px 14px;
    border-bottom: 1px solid ${({ theme }) => theme.border};
`;

const Eyebrow = styled.div`
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.textSubtle};
`;

const RunTitle = styled.div`
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
    margin-top: 6px;
`;

const RunCount = styled.div`
    font-size: 17px;
    font-weight: 650;
    color: ${({ theme }) => theme.text};
    font-variant-numeric: tabular-nums;
`;

const RunPercent = styled.div`
    font-size: 12px;
    font-weight: 600;
    color: ${({ theme }) => theme.textMuted};
    font-variant-numeric: tabular-nums;
`;

const ProgressTrack = styled.div`
    margin-top: 12px;
    height: 4px;
    border-radius: 999px;
    background: ${({ theme }) => theme.surface3};
    overflow: hidden;
`;

const ProgressFill = styled.div`
    height: 100%;
    border-radius: 999px;
    background: ${({ theme }) => theme.accent};
    width: ${({ $pct }) => $pct}%;
    transition: width 320ms ease;

    @media (prefers-reduced-motion: reduce) {
        transition: none;
    }
`;

// Scrolls rather than growing past the viewport: a blueprint can carry a dozen
// domains and the Cancel button must stay reachable.
const DomainList = styled.div`
    max-height: min(46vh, 340px);
    overflow-y: auto;
    padding: 6px 0;
`;

const DomainStatusWrapper = styled.div`
    display: grid;
    grid-template-columns: 18px minmax(0, 1fr) auto;
    align-items: center;
    gap: 10px;
    padding: 9px 20px;
    /* Not theme.accentSoft: that token is color-mix(in oklch, accent, surface),
       and surface is oklch(1 0 0) - white with an explicit hue of 0 - so the mix
       drifts the indigo hue 274 -> 353 and the tint comes out pink. Mixing in
       srgb against transparent keeps the accent's own hue. */
    background: ${({ $state, theme }) =>
        ($state === 'loading' ? `color-mix(in srgb, ${theme.accent} 7%, transparent)` : 'transparent')};
`;

const DomainName = styled.span`
    font-size: 13.5px;
    line-height: 1.35;
    color: ${({ $state, theme }) => ($state === 'skipped' ? theme.textMuted : theme.text)};
    overflow-wrap: anywhere;
`;

const DomainNote = styled.span`
    display: block;
    font-size: 11.5px;
    margin-top: 2px;
    color: ${({ $state, theme }) =>
        $state === 'failed' ? (theme.danger || '#dc2626') : theme.textMuted};
`;

// Tabular figures so the counts line up as a column the eye can scan.
const DomainCount = styled.span`
    font-size: 12px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    color: ${({ $state, theme }) =>
        $state === 'done' ? theme.success
      : $state === 'failed' ? (theme.danger || '#dc2626')
      : theme.textMuted};
`;

const StateMark = styled.span`
    width: 18px;
    height: 18px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    font-weight: 700;
    line-height: 1;
    color: ${({ $state, theme }) =>
        $state === 'done' ? theme.success
      : $state === 'failed' ? (theme.danger || '#dc2626')
      : theme.textSubtle};
`;

const LoadingSpinner = styled.div`
    border: 2px solid ${({ theme }) => theme.surface3};
    border-top: 2px solid ${({ theme }) => theme.accent};
    border-radius: 50%;
    width: 14px;
    height: 14px;
    animation: spin 1s linear infinite;

    @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
    }

    @media (prefers-reduced-motion: reduce) {
        animation: none;
        border-top-color: ${({ theme }) => theme.accent};
    }
`;

const RunFooter = styled.div`
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
    padding: 12px 20px 14px;
    border-top: 1px solid ${({ theme }) => theme.border};
    background: ${({ theme }) => theme.surface2};
`;

const FooterNote = styled.p`
    margin: 0;
    font-size: 11px;
    line-height: 1.45;
    color: ${({ theme }) => theme.textMuted};

    strong {
        color: ${({ theme }) => theme.text};
        font-weight: 700;
    }
`;

const FooterActions = styled.div`
    display: flex;
    justify-content: flex-end;
`;

const Overlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(8, 10, 20, 0.46);
    backdrop-filter: blur(6px);
    /* Above the form Dialog (MUI modal layer ~1410) so the loader covers the
       form during generation while the form stays mounted/open underneath. */
    z-index: 1600;
`;

const assessmentLevels = [
    { label: 'Remember', tooltip: 'Recognizing, Recalling' },
    { label: 'Understand', tooltip: 'Interpreting, Exemplifying, Classifying, Inferring, Comparing, Explaining' },
    { label: 'Apply', tooltip: 'Executing, Implementing' },
    { label: 'Analyze', tooltip: 'Differentiating, Organizing, Attributing' },
    { label: 'Evaluate', tooltip: 'Checking, Critiquing' },
  ];
  
  const gradeLevels = [
    'Kindergarten 1', 'Kindergarten 2', 'Grade 1', 'Grade 2', 'Grade 3', 'Grade 4', 'Grade 5',
    'Grade 6', 'Grade 7', 'Grade 8', 'Grade 9', 'Grade 10', 'Grade 11', 'Grade 12', 'University'
  ];

const languages = [
    'English', 'Spanish', 'French'
  ];

// This dialog inherits the central Instructor design system end-to-end via the
// MUI ThemeProvider in MyRoutes (src/components/Instructor/ui/muiTheme.js):
// Hanken Grotesk font, indigo accent (#5b54e6), neutral-slate text and light
// surfaces. Components are styled by the theme — sx is used for layout only.

// Progress for one generation run, read as the exam blueprint being filled in:
// every selected domain is a row with the question quota it was allocated, so a
// domain that received none reads as "not included" rather than a failure.
const STATE_NOTE = {
    failed: 'Could not generate - it will be left out',
    skipped: 'Not included at this question count',
};

const LoadingPopup = ({ domainStatus, onCancel }) => {
    const requested = domainStatus.reduce((sum, d) => sum + (Number(d.total) || 0), 0);
    const generated = domainStatus.reduce((sum, d) => sum + (Number(d.done) || 0), 0);
    const percent = requested > 0 ? Math.min(100, Math.round((generated / requested) * 100)) : 0;
    const running = domainStatus.some(d => d.isLoading);

    return (
        <Overlay>
            <LoadingPopupWrapper role="dialog" aria-modal="true" aria-label="Generating questions">
                <RunHeader>
                    <Eyebrow>{running ? 'Generating' : 'Generation complete'}</Eyebrow>
                    <RunTitle>
                        <RunCount>
                            {requested > 0
                                ? `${generated} of ${requested} questions`
                                : `${generated} questions`}
                        </RunCount>
                        {requested > 0 && <RunPercent>{percent}%</RunPercent>}
                    </RunTitle>
                    <ProgressTrack
                        role="progressbar"
                        aria-valuenow={percent}
                        aria-valuemin={0}
                        aria-valuemax={100}
                    >
                        <ProgressFill $pct={percent} />
                    </ProgressTrack>
                </RunHeader>

                <DomainList>
                    {domainStatus.map((domain, index) => {
                        const state = domain.isLoading ? 'loading' : (domain.state || 'done');
                        const note = STATE_NOTE[state];
                        const total = Number(domain.total) || 0;
                        const done = Number(domain.done) || 0;
                        return (
                            <DomainStatusWrapper key={index} $state={state}>
                                <StateMark $state={state} aria-hidden="true">
                                    {state === 'loading' ? <LoadingSpinner />
                                      : state === 'failed' ? '\u2715'
                                      : state === 'skipped' ? '\u2013'
                                      : '\u2713'}
                                </StateMark>
                                <DomainName $state={state}>
                                    {domain.name || domain.status}
                                    {note && <DomainNote $state={state}>{note}</DomainNote>}
                                </DomainName>
                                <DomainCount $state={state}>
                                    {total > 0 ? `${done}/${total}` : done || '0'}
                                </DomainCount>
                            </DomainStatusWrapper>
                        );
                    })}
                </DomainList>

                <RunFooter>
                    <FooterNote>
                        <strong>Disclaimer:</strong> StudyAK is an AI-powered content generation tool provided for educational support.
                        While it assists in creating assessments aligned with specific domains and subdomains, it is your responsibility
                        to review and verify the accuracy, appropriateness, and relevance of all generated content before use.
                        StudyAK Inc. makes no guarantees regarding the suitability of AI-generated questions for specific instructional or evaluative purposes.
                    </FooterNote>
                    {onCancel && (
                        <FooterActions>
                            <Btn type="button" $variant="ghost" onClick={onCancel}>
                                Cancel
                            </Btn>
                        </FooterActions>
                    )}
                </RunFooter>
            </LoadingPopupWrapper>
        </Overlay>
    );
};

export default function GenerateQuestiondialog({ open, handleClose, data }) {
    const { guardAction, lockedDialogOpen, setLockedDialogOpen } = useDemoActionGate();
    const [testId, setTestId] = useState('');
    const [examType, setExamType] = useState('Text');
    const [noOfQuestions, setNoOfQuestions] = useState('');
    const [domainStatus, setDomainStatus] = useState([]);
    const [alertMessage, setAlertMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [prompt, setPrompt] = useState('');
    const [questions, setQuestions] = useState('');
    const [isPromptDialogOpen, setPromptDialogOpen] = useState(false);
    const [isQuestionDialogOpen, setQuestionDialogOpen] = useState(false);
    const [isQuestionShowDialogOpen, setQuestionShowDialogOpen] = useState(false);
    const [generatingQuestions, setGeneratingQuestions] = useState(false);
    const [gradeLevel, setGradeLevel] = useState('');
    const [language, setLanguage] = useState('English');
    const [assessmentLevel, setAssessmentLevel] = useState(['Analyze']);
    const [isScenarioBased, setIsScenarioBased] = useState(0);
    const [domainData, setDomainData] = useState([]);
    const [pdfUrls, setPdfUrls] = useState([]);
    const [selectedPdf, setSelectedPdf] = useState(null);
    const [open1, setOpen1] = useState(false);
    const [selectedPdfDetails, setSelectedPdfDetails] = useState(null);
    const userType = localStorage.getItem('userType');
    const [isCaseStudy, setIsCaseStudy] = useState(0);
    // Case-study image: SUBJECT picks which subject row the image prompt applies, and
    // HAZARD_MODE embeds exactly one deliberate safety violation for the question to
    // be about. Neither is stored on the course, so the instructor sets them here.
    const [caseStudySubject, setCaseStudySubject] = useState('MATH');
    const [caseStudyHazardMode, setCaseStudyHazardMode] = useState(false);
    const [passage, setPassage] = useState('');
    const [mediaUrl1, setMediaUrl1] = useState('');
    const [loadingShowPrompt, setLoadingShowPrompt] = useState(false);
    const [loadingGenerate, setLoadingGenerate] = useState(false);
    const apiUrl = process.env.REACT_APP_API_URL;
    const [isDirectQuestion, setIsDirectQuestion] = useState(1); // default checked
const [caseStudyOption, setCaseStudyOption] = useState('generate');
const [showPassageDialog, setShowPassageDialog] = useState(false);
const [generateUsingGradeLevel, setGenerateUsingGradeLevel] = useState(false);
const [caseStudyTitleOrGradle, setCaseStudyTitleOrGradle] = useState('');
const [questionType, setQuestionType] = useState('Direct Questions')
const [editablePrompt, setEditablePrompt] = useState('');
const [isEditPromptDialogOpen, setIsEditPromptDialogOpen] = useState(false);
const [useDirectAttachment, setUseDirectAttachment] = useState(false);
const [originalDomainData, setOriginalDomainData] = useState([]);
const [selectedDomains, setSelectedDomains] = useState([]);
const [selectedSubdomains, setSelectedSubdomains] = useState([]);
const [directAttachmentType, setDirectAttachmentType] = useState(null); // 'pdf' | 'image'
const [selectedDirectAttachment, setSelectedDirectAttachment] = useState(null);
const [loadingEditPrompt, setLoadingEditPrompt] = useState(false);
const [imageUrl, setImageUrl] = useState('');
const [showPromptTextBox, setShowPromptTextBox] = useState(false);
const [extraPromptText, setExtraPromptText] = useState('');
const [caseStudyEditablePrompt, setCaseStudyEditablePrompt] = useState('');
const [isCaseStudyPromptDialogOpen, setIsCaseStudyPromptDialogOpen] = useState(false);
const [loadingCaseStudyEditPrompt, setLoadingCaseStudyEditPrompt] = useState(false);
let mediaUrl = ''

const apiUrl1 = process.env.REACT_APP_API_URL1;
    const token = localStorage.getItem('token');

    const getBasicAuthHeader = () => {
        const username = process.env.REACT_APP_API_USERNAME;
        const password = process.env.REACT_APP_API_PASSWORD;
        const encodedCredentials = btoa(`${username}:${password}`);
        return `Basic ${encodedCredentials}`;
      };
//console.log(domainData)
      useEffect(() => {
        async function getDomainAndSubdomain() {
          const apiUrl = process.env.REACT_APP_API_URL;
      
          try {
              const response = await axios.get(`${apiUrl}/api/AssessmentDomain/${data.testId}`, {
                  headers: {
                      'Content-Type': "application/json",
                      "Accept": "*/*",
                      "Access-Control-Allow-Origin": "*",
                      'Authorization': getBasicAuthHeader(),
                  }
              });
              const domains = response.data;

      setOriginalDomainData(domains);

      // Select all domains by default
      setSelectedDomains(domains.map(d => d.name));

      // Select all subdomains by default
      const allSubs = domains.flatMap(d =>
        d.subDomain?.map(s => s.name) || []
      );
      setSelectedSubdomains(allSubs);

      setDomainData(domains);

          } catch (error) {
              console.error("Error fetching assessment data:", error);
          }
      }
          getDomainAndSubdomain()
      }, [data]);

    const handleGradeLevelChange = (event) => {
        setGradeLevel(event.target.value);
      };

      const handlelanguageChange = (event) => {
        setLanguage(event.target.value);
      };
    
      const handleAssessmentLevelChange = (event) => {
        const { target: { value } } = event;
      
        // Update the state to handle both single and multiple values cleanly
        setAssessmentLevel(Array.isArray(value) ? value : value.split(','));
      };

      const handleDomainChange = (event) => {
  const value = event.target.value;
  setSelectedDomains(value);

  // Get valid subdomains from selected domains
  const validSubdomains = originalDomainData
    .filter(domain => value.includes(domain.name))
    .flatMap(domain =>
      domain.subDomain?.map(sub => sub.name) || []
    );

  setSelectedSubdomains(validSubdomains);

  // Update filtered domainData
  const filteredDomains = originalDomainData
    .filter(domain => value.includes(domain.name))
    .map(domain => ({
      ...domain,
      subDomain: domain.subDomain?.filter(sub =>
        validSubdomains.includes(sub.name)
      )
    }));

  setDomainData(filteredDomains);
};

const handleSubdomainChange = (event) => {
  const value = event.target.value;
  setSelectedSubdomains(value);

  const updatedDomains = originalDomainData
    .filter(domain => selectedDomains.includes(domain.name))
    .map(domain => ({
      ...domain,
      subDomain: domain.subDomain?.filter(sub =>
        value.includes(sub.name)
      )
    }));

  setDomainData(updatedDomains);
};
      
    const handleChange = (field, value) => {
        switch (field) {
            case 'type':
                setExamType(value);
                break;
            case 'noOfQuestions':
                setNoOfQuestions(value);
                break;
            case 'scenario_based':
                setIsScenarioBased(value ? 1 : 0)
                break;
            case 'case_study':
                setIsCaseStudy(value ? 1 : 0)
                break;
            default:
                break;
        }
    };    

    // Assembled server-side from the DB prompt templates, so editing
    // `passage.generate` / `image.caseStudy` in Prompts is reflected here.
    const buildGeneratedCaseStudyPrompt = async () => {
      if (caseStudyOption === 'generate') {
        return PreviewYakiPrompt(buildPassageSpec());
      }

      if (caseStudyOption === 'generateImage') {
        return PreviewYakiPrompt(buildCaseStudyImageSpec());
      }

      return '';
    };

    const hasFieldValue = (value) => {
      if (Array.isArray(value)) return value.length > 0;
      return String(value || '').trim() !== '';
    };

    const validateCaseStudyGenerationFields = () => {
      const missingFields = [];

      if (caseStudyOption === 'generate') {
        if (!hasFieldValue(examType)) missingFields.push('exam type');
        if (!hasFieldValue(gradeLevel)) missingFields.push('grade level');
      } else if (caseStudyOption === 'generateImage') {
        if (!hasFieldValue(gradeLevel)) missingFields.push('grade level');
      } else {
        if (!hasFieldValue(examType)) missingFields.push('exam type');
        if (!hasFieldValue(gradeLevel)) missingFields.push('grade level');
      }

      if (missingFields.length > 0) {
        setAlertMessage(`Please fill ${missingFields.join(' and ')} before generating.`);
        return false;
      }

      return true;
    };

    async function processCaseStudyPdf(selectedPdfDetails, setAlertMessage,caseStudyOption,caseStudyTitleOrGradle,customPrompt = '') {
      //console.log(caseStudyOption)
      if(caseStudyOption=== 'attachment')
      {
    let promptCaseStudy = `You are a language expert. Extract all readable and relevant text from the uploaded PDF.
Ignore headers, footers, watermarks, page numbers, and unnecessary metadata.
Maintain the original paragraph structure and logical flow.
Do not summarize or rephrase anything — just provide the clean, extracted raw text.
Output the extracted text as plain text, formatted cleanly in paragraph form.`;
    if (caseStudyTitleOrGradle) {
  promptCaseStudy += `
After extracting the raw text, rewrite the passage to be suitable for that grade level in terms of vocabulary, sentence complexity, and clarity — while preserving the original meaning for ${caseStudyTitleOrGradle} and the response should contain only the passage no extra text.
`;
}
//console.log(promptCaseStudy)
//console.log(selectedPdfDetails)
    try {
        const downloadResponse = await axios.get(`${apiUrl}/api/download-pdf`, {
            params: { pdfUrl: selectedPdfDetails.pdfUrl },
            headers: { Authorization: getBasicAuthHeader() },
            responseType: "blob",
        });

        const pdfBlob = new Blob([downloadResponse.data], { type: 'application/pdf' });
        const pdfFile = new File([pdfBlob], "document.pdf", { type: 'application/pdf' });

        const formData = new FormData();
        formData.append("file", pdfFile);
        formData.append("prompt", promptCaseStudy);
        formData.append("userId", localStorage.getItem('userId') || '');

        const response = await axios.post(`${apiUrl}/api/v1/openai/process-pdf`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
                'Authorization': getBasicAuthHeader()
            }
        });

        const extractedText = response.data.response[0]; 
        //setPassage(extractedText);
        
        // Wait until passage is set
        await new Promise(resolve => {
            const interval = setInterval(() => {
                if (extractedText) {
                    clearInterval(interval);
                    resolve();
                }
            }, 100);
        });
        return extractedText;

    } catch (error) {
        //console.error("Error processing PDF:", error);
        setAlertMessage("Failed to extract passage from PDF. Please try again.");
        throw error; 
    }
  }
  else{
    if(caseStudyOption=== 'generate')
    {
    // DB template (passage.generate), assembled server-side.
    const prompt = customPrompt || await PreviewYakiPrompt(buildPassageSpec());

    //console.log(prompt,1)
     const body = { prompt, test_id: testId };

            const response = await axios.post(`${apiUrl1}/api/get_question_from_chat_gpt`, body, {
                headers: {
                    'Content-Type': "application/json",
                    'Accept': "*/*",
                    'Authorization': token,
          "X-Api-Version": 'v2'
                }
            });
            //console.log(response.data.data.questionResponse)
            return response.data.data.questionResponse;
        }
        else
        {
          if(caseStudyOption=== 'generateImage')
          {
            //console.log(1,caseStudyOption)
           // DB template (image.caseStudy), assembled server-side.
           const prompt = customPrompt || await PreviewYakiPrompt(buildCaseStudyImageSpec());
//console.log(prompt)
 const body = { prompt, userId: localStorage.getItem('userId') || '' };

            const response = await axios.post(`${apiUrl}/api/v1/openai/generate-image`, body, {
                headers: {
                   'Content-Type': "application/json",
                      "Accept": "*/*",
                      "Access-Control-Allow-Origin": "*",
                      'Authorization': getBasicAuthHeader(),
                }
            });
            //.log(response.data)
            return response.data;
          }
        }
      }
  }

  useEffect(() => {
  if (mediaUrl) {
    //console.log("Media URL updated:", mediaUrl);
  }
}, [mediaUrl]);

 const uploadBase64Image = async (base64StringWithoutPrefix) => {
  try {
    const base64WithPrefix = `data:image/png;base64,${base64StringWithoutPrefix}`;
    //console.log('hitting api')
    const response = await axios.post(
      `${apiUrl}/api/uploadBase64Image`, // URL
      {
        base64: base64WithPrefix // request body
      },
      {
        headers: {
          'Content-Type': 'application/json',
          'Accept': '*/*',
          'Access-Control-Allow-Origin': '*',
          'Authorization': getBasicAuthHeader()
        }
      }
    );

    const extractedText = response.data.fileUrl
    await new Promise(resolve => {
            const interval = setInterval(() => {
                if (extractedText) {
                    clearInterval(interval);
                    resolve();
                }
            }, 100);
        });
      //console.log(response.data)
      mediaUrl=response.data.fileUrl;
      setMediaUrl1(response.data.fileUrl);
        return response.data.fileUrl;
  } catch (error) {
    console.error('Upload failed:', error.response ? error.response.data : error.message);
    return null;
  }
};
function extractStructuredDataFromPrompt(prompt) {
    if (!prompt) return null;

    try {
        // Extract question count
        const questionMatch = prompt.match(/Generate\s+(\d+)/i);
        const totalQuestions = questionMatch ? parseInt(questionMatch[1], 10) : null;

        // Extract Domains JSON block
        const domainMatch = prompt.match(/"Domains"\s*:\s*(\[[\s\S]*?\])\s*\n/);

        if (!domainMatch) return { totalQuestions, domains: [] };

        const domainsJsonString = domainMatch[1];

        const domains = JSON.parse(domainsJsonString);

        return {
            totalQuestions,
            domains
        };

    } catch (error) {
        console.error("Error parsing structured prompt:", error);
        return null;
    }
}

function extractJSONArray(text) {
    const firstBracket = text.indexOf('[');
    const lastBracket = text.lastIndexOf(']');
    
    if (firstBracket === -1 || lastBracket === -1) {
        throw new Error("No valid JSON array found.");
    }

    return text.substring(firstBracket, lastBracket + 1);
}

    // ---- Backend-owned prompt cutover (Integration Plan §15 #1, §3) ----
    // Plain-text MCQ and passage/script cases send a structured spec to the
    // backend (YakiPromptService). PDF (file_search) and image (vision) cases
    // still use the client templates + their own endpoints. The effective
    // passage is passed in so the freshly-extracted case-study text is used
    // rather than the stale `passage` state.
    const isPlainTextGeneration = (effPassage = passage) =>
        !effPassage &&
        !mediaUrl &&
        !imageUrl &&
        !directAttachmentType &&
        caseStudyOption !== 'generateImage' &&
        caseStudyOption !== 'image';

    const isScriptTextGeneration = (effPassage = passage) =>
        !!effPassage &&
        directAttachmentType !== 'pdf' &&
        directAttachmentType !== 'image' &&
        caseStudyOption !== 'generateImage' &&
        caseStudyOption !== 'image';

    const mapDomainToSpec = (d) => ({
        domainId: d.domain_id,
        name: d.name,
        percentage: d.percentage,
        subDomain: (d.subDomain || []).map((s) => ({
            subDomainId: s.sub_domain_id,
            name: s.name,
            subTopics: s.sub_topics
        }))
    });

    const buildSpecBase = () => ({
        testName: data.name,
        mimeType: examType,
        vendor: data.vendor,
        language,
        examTime: String(data.examTime ?? ''),
        totalMarks: String(data.totalMarks ?? ''),
        examCode: data.examCode != null ? String(data.examCode) : '',
        gradeLevel,
        assessmentLevel: Array.isArray(assessmentLevel) ? assessmentLevel.join(',') : (assessmentLevel || ''),
        scenarioBased: !!isScenarioBased,
        extraPromptText: extraPromptText || ''
    });

    // Single-domain spec for the per-domain generation loop.
    const buildSingleDomainSpec = (domain, count) => ({
        ...buildSpecBase(),
        kind: 'question',
        noOfQuestions: count,
        domain: mapDomainToSpec(domain)
    });

    // Single-domain script/passage spec — backend builds the script prompt.
    const buildScriptDomainSpec = (domain, count, scriptPassage) => ({
        ...buildSpecBase(),
        kind: 'scriptQuestion',
        noOfQuestions: count,
        domain: mapDomainToSpec(domain),
        passage: scriptPassage,
        caseStudyOption,
        directAttachmentType,
        mediaUrl: (mediaUrl ? mediaUrl : imageUrl)
    });

    // Multi-domain spec for Show / Edit Prompt preview.
    const buildMultiDomainSpec = (domains, count) => ({
        ...buildSpecBase(),
        kind: 'questionMulti',
        noOfQuestions: count,
        domains: (domains || []).map(mapDomainToSpec)
    });

    // Multi-domain script/passage spec for preview.
    const buildScriptMultiSpec = (domains, count, scriptPassage) => ({
        ...buildSpecBase(),
        kind: 'scriptQuestionMulti',
        noOfQuestions: count,
        domains: (domains || []).map(mapDomainToSpec),
        passage: scriptPassage,
        caseStudyOption,
        directAttachmentType,
        mediaUrl: (mediaUrl ? mediaUrl : imageUrl)
    });

    // Reading-passage spec — the backend renders `passage.generate` from it, so the
    // passage prompt is the DB template (Prompts -> Manually generated questions),
    // not a copy held in the frontend.
    const buildPassageSpec = () => ({
        ...buildSpecBase(),
        kind: 'passage',
        title: caseStudyTitleOrGradle,
        domains: (domainData || []).map(mapDomainToSpec)
    });

    // Case-study stimulus-image spec — the backend renders `image.caseStudy` from it,
    // filling the prompt's FIELDS block from this assessment: course, grade band
    // (derived from gradeLevel), the selected domains/subdomains and their subtopics.
    const buildCaseStudyImageSpec = () => ({
        ...buildSpecBase(),
        kind: 'caseStudyImage',
        courseName: data.courseName || data.name,
        courseCode: data.code || data.courseCode || data.examCode || '',
        subject: caseStudySubject,
        hazardMode: caseStudyHazardMode,
        domains: (domainData || []).map(mapDomainToSpec)
    });

    const yakiCostContext = () => ({
        // Use data.testId (always present) rather than the async testId state,
        // which is still '' during the first generation — otherwise the backend
        // keys the cost row to the course instead of this assessment.
        assessmentId: data.testId || testId,
        courseId: data.courseId,
        courseName: data.courseName,
        assessmentName: data.name
    });

    async function handleGenerateQuestions(useEditedPrompt = false) {

        if (!guardAction('action.assessment.question.aiGenerate')) return;

        if (!useEditedPrompt && (!noOfQuestions && !examType)) {
    setAlertMessage("Please fill the number of questions before question generation.");
    return;
}

        setTestId(data.testId)
let totalQuestions = noOfQuestions;
let filteredDomains='';

if (useEditedPrompt && editablePrompt) {

    const structuredData = extractStructuredDataFromPrompt(editablePrompt);

    if (structuredData) {
        if (structuredData.totalQuestions) {
            totalQuestions = structuredData.totalQuestions;
        }

        if (structuredData.domains && structuredData.domains.length > 0) {
            filteredDomains = structuredData.domains.map(domain => ({
                ...domain,
                subDomain: domain.subDomain || []
            }));
        }
    }
}

if (selectedDomains.length > 0) {
  filteredDomains = originalDomainData
    .filter(d => selectedDomains.includes(d.name))
    .map(d => ({
      ...d,
      subDomain: d.subDomain?.filter(sub =>
        selectedSubdomains.includes(sub.name)
      ) || []
    }));
} else {
  filteredDomains = [];
}

if (useEditedPrompt && editablePrompt) {

  setGeneratingQuestions(true);
  // keep the dialog mounted (see note in the main path below)

  // show loading popup like domain distribution
  setDomainStatus([
    {
      name: "Edited Prompt",
      status: "Generating questions using edited prompt...",
      state: 'loading',
      isLoading: true
    }
  ]);

  try {

    const prompt = editablePrompt;

    const questionsResponse = await GenerateQuestions(
      prompt,
      data.testId || testId,
      "",
      caseStudyOption,
      (mediaUrl?mediaUrl:imageUrl),
      directAttachmentType,
      { assessmentId: data.testId || testId, courseId: data.courseId, courseName: data.courseName, assessmentName: data.name }
    );

    let rawQuestionsString = questionsResponse.replace(/```json|```/g, '');

    const cleanedJson = extractJSONArray(rawQuestionsString);

    const questions = JSON.parse(cleanedJson);

    const finalQuestions = questions.slice(0, totalQuestions);

    setQuestions(finalQuestions);

    // update loader status
    setDomainStatus([
      {
        name: "Edited Prompt",
        status: "Questions generated successfully",
        state: 'done',
        isLoading: false
      }
    ]);
    setEditablePrompt('')
    setQuestionDialogOpen(true);

  } catch (error) {

    console.error("Error generating questions:", error);

    setDomainStatus([
      {
        name: "Edited Prompt",
        status: "Failed to generate questions",
        state: 'failed',
        isLoading: false
      }
    ]);

    setAlertMessage("Failed to generate questions.");
  }

  setGeneratingQuestions(false);

  return;
}
        const questionsList = [];
        setGeneratingQuestions(true); // Start generating questions
        // NOTE: do not handleClose() here — the parent only mounts this dialog
        // while it's "open", so closing it unmounts the loader + results + the
        // Generate button mid-run (the button "disappears"). Keep it mounted; the
        // LoadingPopup overlays the form during generation.

        // How many questions each domain gets. The parts always sum to
        // totalQuestions, and no domain can come out negative or NaN.
        //
        // The old inline maths did Math.floor((percentage / 100) * total) per
        // domain, which returns 0 for every domain whenever total < 100/percentage
        // — e.g. asking for 1 question across 9 domains gave 0 to the first
        // eight. A domain with 0 questions never entered the generation loop, so
        // questionsGeneratedForDomain stayed false and the dialog reported
        // "Failed to generate questions for <domain>" for work it never attempted.
        const allocateQuestions = (domains, total) => {
            const count = domains.length;
            if (count === 0 || total <= 0) return new Array(count).fill(0);

            // Fewer questions than domains: one each to the highest-weighted
            // domains, zero to the rest (those are skipped, not failed).
            if (total <= count) {
                const picked = domains
                    .map((d, idx) => ({ idx, weight: Number(d.percentage) || 0 }))
                    .sort((a, b) => b.weight - a.weight || a.idx - b.idx)
                    .slice(0, total);
                const shares = new Array(count).fill(0);
                picked.forEach(({ idx }) => { shares[idx] = 1; });
                return shares;
            }

            // Proportional split normalised by the actual weight total (the
            // percentages are not guaranteed to add up to 100), then the
            // rounding remainder handed out largest-first so the sum is exact.
            const weights = domains.map(d => Number(d.percentage) || 0);
            const weightTotal = weights.reduce((a, b) => a + b, 0);
            const exact = weights.map(w => (weightTotal > 0 ? w / weightTotal : 1 / count) * total);
            const shares = exact.map(v => Math.floor(v));
            let leftover = total - shares.reduce((a, b) => a + b, 0);
            const byRemainder = exact
                .map((v, idx) => ({ idx, rem: v - Math.floor(v) }))
                .sort((a, b) => b.rem - a.rem || a.idx - b.idx);
            for (let k = 0; leftover > 0; k += 1, leftover -= 1) {
                shares[byRemainder[k % count].idx] += 1;
            }
            return shares;
        };

        const questionAllocation = allocateQuestions(filteredDomains, totalQuestions);

         const initialStatus = filteredDomains.map((domain, idx) => ({
            name: domain.name,
            status: 'Generating questions for ' + domain.name,
            state: questionAllocation[idx] > 0 ? 'loading' : 'skipped',
            done: 0,
            total: questionAllocation[idx],
            isLoading: questionAllocation[idx] > 0
        }));
        setDomainStatus(initialStatus);

       let finalPassage = passage;
let directImageUrl = mediaUrl;

  if (directAttachmentType === 'image') {
    directImageUrl = selectedDirectAttachment.pdfUrl;
  }

    if (isCaseStudy && passage === '') {
        try {
            const extractedText = await processCaseStudyPdf(selectedPdfDetails, setAlertMessage,caseStudyOption,caseStudyTitleOrGradle);
            setPassage(extractedText);
            finalPassage = extractedText;
        } catch (error) {
            setLoadingShowPrompt(false);
            return;
        }
    }
    if (caseStudyOption === 'generateImage') {

  const uploadedUrl = await uploadBase64Image(finalPassage);
}
        let lastGenerationError = null; // surfaced if nothing could be generated


      for (let i = 0; i < filteredDomains.length; i++) {
    const domain = filteredDomains[i];
            const domainName = domain.name;

            const numberOfQuestions = questionAllocation[i];

            // Nothing was asked of this domain, so nothing can fail. Mark it
            // skipped and move on rather than reporting a phantom failure.
            if (numberOfQuestions <= 0) {
                setDomainStatus(prevStatus => {
                    const newStatus = [...prevStatus];
                    newStatus[i] = {
                        name: domainName,
                        status: 'Skipped ' + domainName + ' - no questions allocated',
                        state: 'skipped',
                        done: 0,
                        total: 0,
                        isLoading: false
                    };
                    return newStatus;
                });
                continue;
            }

            let batchSize =15
            // Split large requests into smaller batches
            if(caseStudyOption==='generateImage'||(mediaUrl1!=='' && directAttachmentType === 'image' ))
            {
              batchSize = 4;
            }
            let remainingQuestions = numberOfQuestions;
            let questionsGeneratedForDomain = false;
            let generatedForDomain = 0; // what this domain actually produced
            let attempts = 0;
            const MAX_ATTEMPTS = 3;

            while (remainingQuestions > 0) {
                const questionsInBatch = Math.min(batchSize, remainingQuestions);
                try {
                    attempts += 1;
                    let questionsResponse;
                    if (isPlainTextGeneration(finalPassage)) {
                        // Backend-owned prompt: send a spec, backend assembles + generates + tracks cost.
                        questionsResponse = await GenerateQuestionsFromSpec(
                            buildSingleDomainSpec(domain, questionsInBatch),
                            yakiCostContext()
                        );
                    } else if (isScriptTextGeneration(finalPassage)) {
                        // Passage/script case — backend builds the script prompt.
                        questionsResponse = await GenerateQuestionsFromSpec(
                            buildScriptDomainSpec(domain, questionsInBatch, finalPassage),
                            yakiCostContext()
                        );
                    } else {
                        // PDF (file_search) / image (vision): prompt built server-side,
                        // the file/image still flows through the existing endpoints.
                        const prompt = await PreviewYakiPrompt(buildScriptDomainSpec(domain, questionsInBatch, finalPassage));
                        questionsResponse = await GenerateQuestions(prompt, data.testId || testId,finalPassage,caseStudyOption,(mediaUrl?mediaUrl:imageUrl),directAttachmentType,{ assessmentId: data.testId || testId, courseId: data.courseId, courseName: data.courseName, assessmentName: data.name });
                    }
                    //console.log(questionsResponse)
                    if (!questionsResponse) {
                        // Empty reply = the backend/AI call failed; don't try to parse "".
                        throw new Error('No response from the question service.');
                    }
                    let rawQuestionsString = questionsResponse

                    // Remove the backticks and the surrounding ```json\n...\n``` formatting
                    rawQuestionsString = rawQuestionsString.replace(/```json\n|\n```/g, '');
    
                    // Remove unwanted text before JSON
                    const jsonStartIndex = rawQuestionsString.indexOf('[');
                    if (jsonStartIndex !== -1) {
                        rawQuestionsString = rawQuestionsString.slice(jsonStartIndex);
                    }
    
                    let questions;

try {
    rawQuestionsString = rawQuestionsString.replace(/```json|```/g, '');

    const cleanedJson = extractJSONArray(rawQuestionsString);

    questions = JSON.parse(cleanedJson);
} catch (error) {
    console.error("Raw LLM Response:", rawQuestionsString);
    console.error("JSON Parse Error:", error);

    throw new Error("Invalid JSON returned from AI.");
}
    
                    // The model frequently returns fewer items than asked for.
                    // Counting the REQUESTED size here (instead of what actually
                    // came back) silently left each domain short, and the
                    // end-of-run top-up loop then ground out the shortfall
                    // against the last domain only - which skewed the set and
                    // fired a stream of extra one-question requests.
                    if (!Array.isArray(questions) || questions.length === 0) {
                        throw new Error('AI returned no questions.');
                    }
                    questions.forEach(question => questionsList.push(question));
                    generatedForDomain += questions.length;
                    remainingQuestions -= questions.length;
                    attempts = 0; // reset the retry budget after a successful batch

                    const doneSoFar = generatedForDomain; // snapshot: the updater may run later
                    setDomainStatus(prevStatus => {
                        const newStatus = [...prevStatus];
                        newStatus[i] = {
                            name: domainName,
                            status: 'Questions generated for ' + domainName,
                            state: 'done',
                            done: doneSoFar,
                            total: numberOfQuestions,
                            isLoading: false
                        };
                        return newStatus;
                    });

                    questionsGeneratedForDomain = true;
                } catch (error) {
                    console.error("Error generating questions for domain:", domainName, error);
                    lastGenerationError = error;

                    // Give up on this domain after a few attempts instead of
                    // retrying forever (which made the dialog appear to hang).
                    if (attempts >= MAX_ATTEMPTS) {
                        const doneSoFar = generatedForDomain; // snapshot: the updater may run later
                        setDomainStatus(prevStatus => {
                            const newStatus = [...prevStatus];
                            newStatus[i] = {
                                name: domainName,
                                status: 'Failed to generate questions for ' + domainName,
                                state: 'failed',
                                done: doneSoFar,
                                total: numberOfQuestions,
                                isLoading: false
                            };
                            return newStatus;
                        });
                        break;
                    }

                    // Update status to indicate error but continue to retry
                    const doneSoFar = generatedForDomain; // snapshot: the updater may run later
                    setDomainStatus(prevStatus => {
                        const newStatus = [...prevStatus];
                        newStatus[i] = {
                            name: domainName,
                            status: `Retrying to generate questions for ${domainName} (attempt ${attempts + 1}/${MAX_ATTEMPTS})`,
                            state: 'loading',
                            done: doneSoFar,
                            total: numberOfQuestions,
                            isLoading: true
                        };
                        return newStatus;
                    });
                }
            }
    
            if (!questionsGeneratedForDomain) {
                // Final error status if unable to generate questions after retries
                const doneSoFar = generatedForDomain; // snapshot: the updater may run later
                setDomainStatus(prevStatus => {
                    const newStatus = [...prevStatus];
                    newStatus[i] = {
                        name: domainName,
                        status: 'Failed to generate questions for ' + domainName,
                        state: 'failed',
                        done: doneSoFar,
                        total: numberOfQuestions,
                        isLoading: false
                    };
                    return newStatus;
                });
            }
        }
    
        // Adjust the number of questions in questionsList to exactly match totalQuestions
        while (questionsList.length > totalQuestions) {
            questionsList.pop();
        }
    
        while (questionsList.length < totalQuestions) {
            // Generate additional questions for the last domain if fewer questions were generated
            const lastDomain = filteredDomains[filteredDomains.length - 1];
            const remainingQuestions = totalQuestions - questionsList.length;
            try {
                let questionsResponse;
                if (isPlainTextGeneration(finalPassage)) {
                    questionsResponse = await GenerateQuestionsFromSpec(
                        buildSingleDomainSpec(lastDomain, remainingQuestions),
                        yakiCostContext()
                    );
                } else if (isScriptTextGeneration(finalPassage)) {
                    questionsResponse = await GenerateQuestionsFromSpec(
                        buildScriptDomainSpec(lastDomain, remainingQuestions, finalPassage),
                        yakiCostContext()
                    );
                } else {
                    // PDF / image: prompt built server-side; file/image via existing endpoints.
                    const prompt = await PreviewYakiPrompt(buildScriptDomainSpec(lastDomain, remainingQuestions, finalPassage));
                    questionsResponse = await GenerateQuestions(prompt, testId,finalPassage,caseStudyOption,(mediaUrl?mediaUrl:imageUrl),directAttachmentType,{ courseId: data.courseId, courseName: data.courseName, assessmentName: data.name });
                }

                let rawQuestionsString = questionsResponse
                rawQuestionsString = rawQuestionsString.replace(/```json\n|\n```/g, '');
    
                const jsonStartIndex = rawQuestionsString.indexOf('[');
                if (jsonStartIndex !== -1) {
                    rawQuestionsString = rawQuestionsString.slice(jsonStartIndex);
                }
    
                const additionalQuestions = JSON.parse(rawQuestionsString);
                additionalQuestions.forEach(question => questionsList.push(question));
            } catch (error) {
                console.error("Error generating additional questions for the last domain:", error);
                setAlertMessage("Failed to generate the exact number of questions. Please try again.");
                break;
            }
        }
    
        if (questionsList.length === 0) {
            setAlertMessage(
                lastGenerationError
                    ? `No questions generated: ${lastGenerationError.message || 'the AI service did not respond.'} Please try again.`
                    : "No questions generated, please try again."
            );
        } else {
            setQuestions(questionsList);
            //console.log(questionsList)
            setQuestionDialogOpen(true); // Open the question dialog to show the generated questions
        }
        if(directAttachmentType)
        {
          setPassage('')
        }
        setGeneratingQuestions(false); // Questions generation completed
    }
    
    const handleSave = async (editedContent, passage,caseStudyOption,mediaUrl) => {
    if (!guardAction('action.assessment.question.aiSave')) return;
    try {
        setLoading(true);
        //console.log(editedContent)
        const payload = {
            passage: passage,
            questions: editedContent,
            caseStudyOption: caseStudyOption,
            mediaUrl:(mediaUrl?mediaUrl:imageUrl)
        };

        const response = await fetch(`${apiUrl}/api/generate_question/${testId}`, {
            method: 'POST',
            body: JSON.stringify(payload),
            headers: {
                'Content-Type': 'application/json',
                'Authorization': getBasicAuthHeader(),
            }
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error('Failed to insert questions: ' + errorText);
        }

    } catch (error) {
        console.error('Error inserting questions:', error);
    } finally {
        setLoading(false);
    }
};

    const renderQuestions = () => {
        if (loading) {
            return <CircularProgress />; // Display loading indicator
        } else if (questions) {
            // return your rendered questions here
        } else {
            return null; // Return nothing if no questions available
        }
    };
const showGeneratePrompt = async (data) => {
    setLoadingShowPrompt(true);
    let finalPassage = passage;
    //console.log(isCaseStudy,passage)
    if (isCaseStudy && passage === '') {
        try {
          //console.log(2)
            const extractedText = await processCaseStudyPdf(selectedPdfDetails, setAlertMessage,caseStudyOption,caseStudyTitleOrGradle);
            setPassage(extractedText);      // update state for future reference
            finalPassage = extractedText;   // use it immediately
        } catch (error) {
            setLoadingShowPrompt(false);
            return;
        }
    }

    // Every branch previews the DB template assembled server-side — including the
    // attachment cases, which generate with buildScriptDomainSpec, so what is shown
    // here is what actually runs.
    let prompt;
    try {
        const previewSpec = isPlainTextGeneration(finalPassage)
            ? buildMultiDomainSpec(domainData, noOfQuestions)
            : buildScriptMultiSpec(domainData, noOfQuestions, finalPassage);
        prompt = await PreviewYakiPrompt(previewSpec);
    } catch (error) {
        console.error('Error fetching Yaki prompt preview:', error);
        setAlertMessage('Failed to load the prompt preview.');
        setLoadingShowPrompt(false);
        return;
    }
    setPrompt(prompt);
    setPromptDialogOpen(true);
    setLoadingShowPrompt(false);
};

const getFileType = (url) => {
  if (!url) return "file";

  const extension = url.split(".").pop().toLowerCase();

  const imageExtensions = [
    "jpg",
    "jpeg",
    "png",
    "gif",
    "webp",
    "bmp",
    "svg"
  ];

  if (imageExtensions.includes(extension)) return "image";
  if (extension === "pdf") return "pdf";
  if (extension === "doc" || extension === "docx") return "doc";
  if (extension === "xls" || extension === "xlsx") return "excel";
  if (extension === "ppt" || extension === "pptx") return "ppt";

  return extension;
};

const EditPrompt = async () => {
    setLoadingEditPrompt(true);
    let finalPassage = passage;
    //console.log(isCaseStudy,passage)
    if (directAttachmentType==='pdf' && passage === '') {
        try {
          //console.log(2)
            const extractedText = await processCaseStudyPdf( selectedDirectAttachment,
        setAlertMessage,
        directAttachmentType,
        gradeLevel);
            setPassage(extractedText);      // update state for future reference
            finalPassage = extractedText;   // use it immediately
        } catch (error) {
            setLoadingEditPrompt(false);
            return;
        }
    }

    //console.log('mediaUrl',mediaUrl,imageUrl)
    // As above: always the server-assembled DB template, attachment cases included.
    let prompt;
    try {
        const previewSpec = isPlainTextGeneration(finalPassage)
            ? buildMultiDomainSpec(domainData, noOfQuestions)
            : buildScriptMultiSpec(domainData, noOfQuestions, finalPassage);
        prompt = await PreviewYakiPrompt(previewSpec);
    } catch (error) {
        console.error('Error fetching Yaki prompt preview:', error);
        setAlertMessage('Failed to load the prompt preview.');
        setLoadingEditPrompt(false);
        return;
    }

    setSelectedDomains(domainData.map(d => d.name));

const allSubdomains = domainData.flatMap(d =>
  d.subDomain?.map(s => s.name) || []
);

setSelectedSubdomains(allSubdomains);


    setEditablePrompt(prompt);
    setIsEditPromptDialogOpen(true);
    setLoadingEditPrompt(false);
};

  const fetchPdfUrls = async () => {
    try {
      const source="Instructor"
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await axios.get(`${apiUrl}/api/pdf-urls/${data.courseId}/${source}`, {
        headers: { Authorization: getBasicAuthHeader() },
      });
      //console.log(response.data)
      if (response.data.length === 0) {
        alert("No PDFs available.");
        setOpen1(false); // Close dialog if no PDFs available
      } else {
        setPdfUrls(response.data);
      }
    } catch (err) {
      console.error("Error fetching PDFs:", err);
      setPdfUrls([]); // Reset the PDF list on error
    }
  };

  // Effect to fetch PDFs when dialog opens
  useEffect(() => {
    if (open1) {
      fetchPdfUrls();
    }
    return () => {
      // Cleanup to reset PDF list when dialog closes or component unmounts
      setPdfUrls([]);
      setSelectedPdf(null);
    };
  }, [open1]);

  const handleGenerateClick = () => {
    setOpen1(true); // Open the dialog
  };

  const handleDialogClose = () => {
    
    setOpen1(false); // Close the dialog
  };

  const handlePdfSelect = (pdfId) => {
    setSelectedPdf(pdfId);
  };

const handleOkClick = () => {
  if (!selectedPdf) {
    alert("Please select an attachment before clicking OK.");
    return;
  }

  const selectedFile = pdfUrls.find((file) => file.pdfId === selectedPdf);
  if (!selectedFile) return;

  const fileUrl = selectedFile.pdfUrl;
  // ✅ Detect type from URL (NOT name)
  let type = null;

  if (fileUrl) {
    const extension = fileUrl.split('.').pop().toLowerCase();
    mediaUrl=fileUrl;
    setImageUrl(fileUrl)
    if (extension === 'pdf') {
      type = 'pdf';
    } else if (
      extension === 'png' ||
      extension === 'jpg' ||
      extension === 'jpeg' ||
      extension === 'webp'
    ) {
      type = 'image';
    }
  }
  // Fallback safety
  if (!type) {
    alert("Unsupported file type selected.");
    return;
  }
  // ✅ Set values based on question type
  if (isDirectQuestion === 1) {
    setSelectedDirectAttachment(selectedFile);
    setDirectAttachmentType(type);
    setMediaUrl1(fileUrl);
  } else if (isCaseStudy === 1) {
    setSelectedPdfDetails(selectedFile);
    setMediaUrl1(fileUrl);
  }
  handleDialogClose();
};

  const handleShowCaseStudy = () => {
  setShowPassageDialog(true)
};

const handleEditCaseStudyPrompt = async () => {
  if (!validateCaseStudyGenerationFields()) {
    return;
  }

  setLoadingCaseStudyEditPrompt(true);
  try {
    setCaseStudyEditablePrompt(await buildGeneratedCaseStudyPrompt());
    setIsCaseStudyPromptDialogOpen(true);
  } catch (error) {
    console.error('Error fetching the case-study prompt preview:', error);
    setAlertMessage('Failed to load the prompt preview.');
  } finally {
    setLoadingCaseStudyEditPrompt(false);
  }
};

const handleGenerateCaseStudy = async (customPrompt = '') => {
  if (!guardAction('action.assessment.question.aiGenerate')) return;
  if (!validateCaseStudyGenerationFields()) {
    return;
  }

  if (isCaseStudy) {
    try {
      setLoadingGenerate(true);

      // Clear passage
      setPassage('');

      if(caseStudyOption==='attachment')
      {
        setCaseStudyTitleOrGradle(gradeLevel)
        //console.log(caseStudyTitleOrGradle)
      }
      
      // Now generate new passage
      const extractedText = await processCaseStudyPdf(
        selectedPdfDetails,
        setAlertMessage,
        caseStudyOption,
        caseStudyTitleOrGradle,
        customPrompt
      );

      setPassage(extractedText);
      setLoadingGenerate(false);
      setShowPassageDialog(true)
    } catch (error) {
      console.error('Error generating case study:', error);
      setLoadingGenerate(false);
    }
  }
};

// Layout-only helpers. Colours, radii, shadows, focus rings and typography all
// come from the central Instructor MUI theme (src/components/Instructor/ui/muiTheme.js)
// so this dialog renders identically to the rest of the site.
const compactEditPromptButtonSx = {
  minWidth: 0,
  width: 34,
  height: 34,
  p: 0,
  '& .MuiButton-startIcon': { mr: 0, ml: 0 },
};

const quickActionButtonSx = {
  minWidth: 104,
};

const PORTAL_ACCENT = '#5b54e6';
const PORTAL_ACCENT_SOFT = 'rgba(91, 84, 230, 0.1)';

const sectionPanelSx = {
  p: { xs: 2, sm: 2.75 },
  borderRadius: '16px',
  backgroundColor: 'background.paper',
  border: '1px solid',
  borderColor: 'divider',
  boxShadow: '0 1px 2px rgba(17, 20, 38, 0.04), 0 6px 16px -10px rgba(17, 20, 38, 0.10)',
};

const sectionHeaderRowSx = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 1.25,
  mb: 2,
};

const sectionIconBadgeSx = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  width: 30,
  height: 30,
  borderRadius: '9px',
  backgroundColor: 'rgba(91, 84, 230, 0.1)',
  border: '1px solid rgba(91, 84, 230, 0.18)',
};

const sectionTitleSx = {
  color: 'text.primary',
  fontWeight: 800,
  fontSize: '0.95rem',
  lineHeight: 1.3,
  letterSpacing: '-0.01em',
};

const sectionSubtitleSx = {
  mt: 0.25,
  color: 'text.secondary',
  fontSize: '0.78rem',
  lineHeight: 1.45,
  fontWeight: 500,
};

const MAX_VISIBLE_CHIPS = 4;

const multiSelectChipSx = {
  height: 24,
  borderRadius: '999px',
  fontWeight: 600,
  fontSize: '0.72rem',
  borderColor: PORTAL_ACCENT,
  color: 'text.primary',
  backgroundColor: PORTAL_ACCENT_SOFT,
  '& .MuiChip-label': {
    px: 1,
  },
};

const renderMultiSelectChips = (selected) => {
  if (!selected.length) {
    return (
      <Typography component="span" sx={{ color: 'text.secondary', fontSize: '0.84rem' }}>
        None selected
      </Typography>
    );
  }

  const visible = selected.slice(0, MAX_VISIBLE_CHIPS);
  const overflow = selected.length - visible.length;
  const fullLabel = selected.join(', ');

  return (
    <Tooltip title={fullLabel} arrow placement="top">
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 0.5,
          py: 0.25,
          maxHeight: 72,
          overflow: 'hidden',
        }}
      >
        {visible.map((value) => (
          <Chip key={value} label={value} size="small" variant="outlined" sx={multiSelectChipSx} />
        ))}
        {overflow > 0 && (
          <Chip
            label={`+${overflow} more`}
            size="small"
            variant="outlined"
            sx={{
              ...multiSelectChipSx,
              borderStyle: 'dashed',
              backgroundColor: 'transparent',
            }}
          />
        )}
      </Box>
    </Tooltip>
  );
};

const fieldControlSx = {};

const optionRowSx = (selected, disabled = false) => ({
  width: '100%',
  m: 0,
  px: 1.5,
  py: 1.15,
  boxSizing: 'border-box',
  borderRadius: '10px',
  alignItems: 'flex-start',
  border: '1px solid',
  borderColor: selected ? PORTAL_ACCENT : 'divider',
  backgroundColor: selected ? PORTAL_ACCENT_SOFT : 'background.paper',
  opacity: disabled ? 0.6 : 1,
  transition: 'border-color 0.18s ease, background 0.18s ease',
  '&:hover': {
    borderColor: disabled ? 'divider' : PORTAL_ACCENT,
  },
  '& .MuiFormControlLabel-label': {
    width: '100%',
  },
  '& .MuiRadio-root': {
    p: '6px',
    color: selected ? PORTAL_ACCENT : 'text.secondary',
    '&.Mui-checked': {
      color: PORTAL_ACCENT,
    },
  },
});

const questionStyleGridSx = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
  gap: 1.5,
  width: '100%',
};

const questionTypePillSx = (selected, disabled = false) => ({
  m: 0,
  mx: 0,
  px: 1.5,
  py: 1.15,
  width: '100%',
  boxSizing: 'border-box',
  borderRadius: '12px',
  border: '1px solid',
  borderColor: selected ? PORTAL_ACCENT : 'divider',
  backgroundColor: selected ? PORTAL_ACCENT_SOFT : 'background.paper',
  color: disabled ? 'text.disabled' : 'text.primary',
  transition: 'border-color 0.18s ease, background 0.18s ease, box-shadow 0.18s ease',
  alignItems: 'flex-start',
  boxShadow: selected ? '0 0 0 1px rgba(91, 84, 230, 0.08)' : 'none',
  '&:hover': {
    borderColor: disabled ? 'divider' : PORTAL_ACCENT,
  },
  '& .MuiCheckbox-root, & .MuiRadio-root': {
    p: '4px',
    mt: '2px',
    color: selected ? PORTAL_ACCENT : 'text.secondary',
    '&.Mui-checked': {
      color: PORTAL_ACCENT,
    },
  },
  '& .MuiFormControlLabel-label': {
    width: '100%',
  },
});

const questionTypeLabelSx = {
  display: 'flex',
  flexDirection: 'column',
  gap: 0.35,
  pt: 0.15,
};

const questionTypeTitleSx = {
  fontWeight: 700,
  fontSize: '0.86rem',
  lineHeight: 1.3,
  color: 'text.primary',
};

const questionTypeHintSx = {
  fontWeight: 500,
  fontSize: '0.72rem',
  lineHeight: 1.35,
  color: 'text.secondary',
};

const subsectionDividerSx = {
  gridColumn: '1 / -1',
  pt: 1.75,
  mt: 0.75,
  borderTop: '1px solid',
  borderColor: 'divider',
};

const directAttachmentPanelSx = {
  ...subsectionDividerSx,
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
};

const directAttachmentDropzoneSx = (hasAttachment) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 1.25,
  flexWrap: 'wrap',
  px: 1.5,
  py: 1.25,
  borderRadius: '12px',
  border: '1px dashed',
  borderColor: hasAttachment ? PORTAL_ACCENT : 'divider',
  backgroundColor: hasAttachment ? PORTAL_ACCENT_SOFT : 'background.default',
});

const caseStudyPanelSx = {
  ...subsectionDividerSx,
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
};

const promptToolbarSx = {
  gridColumn: '1 / -1',
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: 1,
  mt: 1.5,
  pt: 1.5,
  px: 1.25,
  pb: 0.25,
  borderRadius: '10px',
  borderTop: '1px solid',
  borderColor: 'divider',
  backgroundColor: 'background.default',
};

const renderCaseStudyEditPromptButton = () => (
  userType === '2' ? (
  <Tooltip title="Edit prompt before generating" arrow placement="top">
    <span>
      <Button
        aria-label="Edit prompt"
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          handleEditCaseStudyPrompt();
        }}
        variant="contained"
        size="small"
        startIcon={
          loadingCaseStudyEditPrompt ? (
            <ButtonSpinner size={12} />
          ) : (
            <EditNoteIcon sx={{ fontSize: 16 }} />
          )
        }
        disabled={loadingGenerate || loadingCaseStudyEditPrompt}
        sx={compactEditPromptButtonSx}
      />
    </span>
  </Tooltip>
  ) : null
);

    return (
        <>
            <QuestionDialog open={isQuestionDialogOpen && !generatingQuestions} handleClose={() => { setQuestionDialogOpen(false); handleClose(); }} questions={questions} onSave={handleSave} testId={testId} passage={passage} caseStudyOption={caseStudyOption} mediaUrl={mediaUrl1 ? mediaUrl1 : mediaUrl} questionType={questionType} assessmentLevel={assessmentLevel} directAttachmentType={directAttachmentType}/>
            <QuestionsShowdialog open={isQuestionShowDialogOpen} handleClose={() => setQuestionShowDialogOpen(false)} testId={testId} />
            <PromptDialog open={isPromptDialogOpen} handleClose={() => setPromptDialogOpen(false)} prompt={prompt} />
            <PassageDialog open={showPassageDialog} handleClose={() => setShowPassageDialog(false)} passage={passage} caseStudyOption={caseStudyOption}/>
            <Dialog
              open={open && !isQuestionDialogOpen}
              onClose={handleClose}
              aria-labelledby="alert-dialog-title"
              aria-describedby="alert-dialog-description"
              maxWidth="md"
              fullWidth
            >
  <Dialog
  open={isEditPromptDialogOpen}
  onClose={() => setIsEditPromptDialogOpen(false)}
  fullWidth
  maxWidth="md"
>
  <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    <EditNoteIcon fontSize="small" />
    Edit Prompt Before Generating
  </DialogTitle>

  <DialogContent dividers>
    <TextField
      multiline
      rows={15}
      fullWidth
      value={editablePrompt}
      onChange={(e) => setEditablePrompt(e.target.value)}
      variant="outlined"
      sx={{ mt: 1 }}
    />
  </DialogContent>

  <DialogActions>
    <Button
      onClick={() => setIsEditPromptDialogOpen(false)}
      variant="outlined"
      sx={quickActionButtonSx}
    >
      Cancel
    </Button>

    <Button
      onClick={() => {
        setIsEditPromptDialogOpen(false);
        handleGenerateQuestions(true); // pass flag to use edited prompt
      }}
      variant="contained"
      color="primary"
      sx={{ ...quickActionButtonSx, minWidth: 180 }}
    >
      Generate With This Prompt
    </Button>
  </DialogActions>
</Dialog>

  <Dialog
  open={isCaseStudyPromptDialogOpen}
  onClose={() => setIsCaseStudyPromptDialogOpen(false)}
  fullWidth
  maxWidth="md"
>
  <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    <EditNoteIcon fontSize="small" />
    {caseStudyOption === 'generateImage'
      ? 'Edit Image Prompt'
      : 'Edit Passage Prompt'}
  </DialogTitle>

  <DialogContent dividers>
    <TextField
      multiline
      rows={15}
      fullWidth
      value={caseStudyEditablePrompt}
      onChange={(e) => setCaseStudyEditablePrompt(e.target.value)}
      variant="outlined"
      sx={{ mt: 1 }}
    />
  </DialogContent>

  <DialogActions>
    <Button
      onClick={() => setIsCaseStudyPromptDialogOpen(false)}
      variant="outlined"
      disabled={loadingGenerate}
    >
      Cancel
    </Button>

    <Button
      onClick={async () => {
        setIsCaseStudyPromptDialogOpen(false);
        await handleGenerateCaseStudy(caseStudyEditablePrompt);
      }}
      variant="contained"
      color="primary"
      disabled={loadingGenerate}
    >
      {loadingGenerate ? (
        <ButtonSpinnerSlot size={16} />
      ) : (
        'Generate With This Prompt'
      )}
    </Button>
  </DialogActions>
</Dialog>

                <DialogTitle id="alert-dialog-title">
                  {data ? 'Generate Question' : 'Create Assessment'}
                  <Typography
                    sx={{ mt: 0.5, fontSize: 12.5, fontWeight: 500, color: 'text.secondary', lineHeight: 1.4 }}
                  >
                    Configure the question set, source material, and AI prompt options.
                  </Typography>
                </DialogTitle>
                <DialogContent dividers sx={{ backgroundColor: 'background.default' }}>
                    <Grid
                      container
                      spacing={2.5}
                      sx={{
                        m: 0,
                        width: '100%',
                      }}
                    >
<Grid item xs={12}>
  <Box sx={sectionPanelSx}>
    <Box sx={sectionHeaderRowSx}>
      <Box sx={sectionIconBadgeSx}>
        <AutoAwesomeIcon sx={{ fontSize: 17, color: 'primary.main' }} />
      </Box>
      <Box>
        <Typography sx={sectionTitleSx}>Assessment Setup</Typography>
        <Typography sx={sectionSubtitleSx}>
          Set exam size, grade, difficulty, and language before choosing domains.
        </Typography>
      </Box>
    </Box>
    <Grid container spacing={2}>
<Grid item xs={12} sm={6}>
  <FormControl fullWidth variant="outlined" margin="dense">
    <InputLabel
      htmlFor="noOfQuestions"
      sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
    >
      Number of Questions
      <FieldHelp text={HELP.noOfQuestions} />
    </InputLabel>
    <OutlinedInput
      id="noOfQuestions"
      value={noOfQuestions}
      onChange={(e) => handleChange('noOfQuestions', e.target.value)}
      label="Number of Questions"
      sx={fieldControlSx}
      inputProps={{ style: { paddingTop: '12px' } }}
    />
  </FormControl>
</Grid>

                        <Grid item xs={12} sm={6}>
                        <FormControl fullWidth variant="outlined" margin="dense">
  <InputLabel
    htmlFor="grade_level"
    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
  >
    Grade Level
    <FieldHelp text={HELP.gradeLevel} />
  </InputLabel>
  <Select
    id="grade_level"
    value={gradeLevel || ''}
    onChange={handleGradeLevelChange}
    label="Grade Level"
    sx={fieldControlSx}
  >
    <MenuItem value=""><em>None</em></MenuItem>
    {gradeLevels.map((level) => (
      <MenuItem key={level} value={level}>
        {level}
      </MenuItem>
    ))}
  </Select>
</FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                        <FormControl fullWidth variant="outlined" margin="dense">
  <InputLabel
    htmlFor="assessment_level"
    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
  >
    Assessment Level
    <FieldHelp text={HELP.assessmentLevel} />
  </InputLabel>
  <Select
    id="assessment_level"
    multiple
    value={assessmentLevel || []}
    onChange={handleAssessmentLevelChange}
    renderValue={renderMultiSelectChips}
    sx={{
      ...fieldControlSx,
      '& .MuiSelect-select': {
        whiteSpace: 'normal',
        minHeight: '1.5em',
      },
    }}
  >
    {assessmentLevels.map((level) => (
      <MenuItem key={level.label} value={level.label}>
        <Checkbox checked={assessmentLevel.includes(level.label)} />
        <ListItemText primary={level.label} secondary={level.tooltip} />
      </MenuItem>
    ))}
  </Select>
</FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                        <FormControl fullWidth variant="outlined" margin="dense">
  <InputLabel
    htmlFor="Language"
    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
  >
    Language
    <FieldHelp text={HELP.language} />
  </InputLabel>
  <Select id="Language" value={language || ''} 
  onChange={handlelanguageChange} 
  label="Language" 
  sx={fieldControlSx} >
   {languages.map((l) => ( <MenuItem key={l} value={l}> {l} </MenuItem> ))} </Select>
</FormControl>
                        </Grid>
<Grid item xs={12} sx={{ pt: 0.5, pb: 0.5 }}>

  {/* Add Text to Prompt Button */}
  {!showPromptTextBox && (
    <Button
      variant="outlined"
      color="primary"
      size="small"
      onClick={() => setShowPromptTextBox(true)}
      sx={{ mt: 0.5 }}
    >
      Add Text to Prompt
    </Button>
  )}

  {/* Smooth Animated Textbox */}
  <Collapse in={showPromptTextBox} timeout={400} unmountOnExit>

    <Box
      mt={2}
      sx={{
        borderRadius: '12px',
        p: 2,
        backgroundColor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >

      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Typography variant="subtitle1">
          Add Text to Prompt
        </Typography>

        <IconButton
          color="error"
          size="small"
          onClick={() => {
            setShowPromptTextBox(false);
            setExtraPromptText('');
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Textbox */}
      <TextField
        fullWidth
        multiline
        rows={4}
        value={extraPromptText}
        onChange={(e) => setExtraPromptText(e.target.value)}
        placeholder="Enter additional instructions to be added to the prompt..."
        sx={{ mt: 2 }}
      />

    </Box>

  </Collapse>

</Grid>
</Grid>
  </Box>
</Grid>
<Grid item xs={12}>
  <Box sx={sectionPanelSx}>
    <Box sx={sectionHeaderRowSx}>
      <Box sx={sectionIconBadgeSx}>
        <EditNoteIcon sx={{ fontSize: 17, color: 'primary.main' }} />
      </Box>
      <Box>
        <Typography sx={sectionTitleSx}>Domain Coverage</Typography>
        <Typography sx={sectionSubtitleSx}>
          Choose which domains and subdomains the AI should draw questions from.
        </Typography>
      </Box>
    </Box>
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <FormControl fullWidth variant="outlined" margin="dense">
          <InputLabel>Domain</InputLabel>
          <Select
            multiple
            value={selectedDomains}
            onChange={handleDomainChange}
            renderValue={renderMultiSelectChips}
            sx={{
              ...fieldControlSx,
              '& .MuiSelect-select': {
                whiteSpace: 'normal',
                minHeight: '2.75em',
                py: 0.75,
              },
            }}
          >
            {originalDomainData.map((domain) => (
              <MenuItem key={domain.name} value={domain.name}>
                <Checkbox checked={selectedDomains.includes(domain.name)} />
                <ListItemText primary={domain.name} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <FormControl fullWidth variant="outlined" margin="dense">
          <InputLabel>Subdomain</InputLabel>
          <Select
            multiple
            value={selectedSubdomains}
            onChange={handleSubdomainChange}
            renderValue={renderMultiSelectChips}
            sx={{
              ...fieldControlSx,
              '& .MuiSelect-select': {
                whiteSpace: 'normal',
                minHeight: '2.75em',
                py: 0.75,
              },
            }}
          >
            {originalDomainData
              .filter(domain => selectedDomains.includes(domain.name))
              .flatMap(domain =>
                domain.subDomain?.map(sub => (
                  <MenuItem key={sub.name} value={sub.name}>
                    <Checkbox checked={selectedSubdomains.includes(sub.name)} />
                    <ListItemText primary={sub.name} />
                  </MenuItem>
                )) || []
              )}
          </Select>
        </FormControl>
      </Grid>
    </Grid>
  </Box>
</Grid>
<Grid item xs={12}>
  <Box sx={{ ...sectionPanelSx, height: '100%' }}>
    <Box sx={sectionHeaderRowSx}>
      <Box sx={sectionIconBadgeSx}>
        <EditNoteIcon sx={{ fontSize: 17, color: 'primary.main' }} />
      </Box>
      <Box>
        <Typography sx={sectionTitleSx}>Question Style</Typography>
        <Typography sx={sectionSubtitleSx}>
          Pick how questions are framed, then attach source material or tune the AI prompt.
        </Typography>
      </Box>
    </Box>
                        <FormControl fullWidth margin="dense" sx={{ mt: 0.5 }}>
    <Box sx={questionStyleGridSx}>
      {/* Direct Questions */}
      <FormControlLabel
        sx={questionTypePillSx(isDirectQuestion === 1, isCaseStudy === 1)}
        control={
          <Checkbox
            checked={isDirectQuestion === 1}
            onChange={(e) => {
              const checked = e.target.checked;
              setIsDirectQuestion(checked ? 1 : 0);
              setQuestionType('Direct Questions')
              if (checked) {
  setSelectedPdfDetails(null);
  setSelectedPdf(null);
}
              if (checked && isCaseStudy === 1) setIsCaseStudy(0);
            }}
            disabled={isCaseStudy === 1}
          />
        }
        label={
          <Box sx={questionTypeLabelSx}>
            <Typography component="span" sx={questionTypeTitleSx}>Direct Questions</Typography>
            <Typography component="span" sx={questionTypeHintSx}>Standalone items without a shared passage</Typography>
          </Box>
        }
      />

      {/* Scenario-based Questions */}
      <FormControlLabel
        sx={questionTypePillSx(isScenarioBased === 1, isCaseStudy === 1)}
        control={
          <Checkbox
            checked={isScenarioBased === 1}
            onChange={(e) => {
              const checked = e.target.checked;
              setIsScenarioBased(checked ? 1 : 0);
              setQuestionType('Scenario Based')
              if (checked && isCaseStudy === 1) setIsCaseStudy(0);
            }}
            disabled={isCaseStudy === 1}
          />
        }
        label={
          <Box sx={questionTypeLabelSx}>
            <Typography component="span" sx={questionTypeTitleSx}>Scenario-based Questions</Typography>
            <Typography component="span" sx={questionTypeHintSx}>Short situational context per question</Typography>
          </Box>
        }
      />

      {/* Case Study Questions */}
      <FormControlLabel
        sx={questionTypePillSx(isCaseStudy === 1)}
        control={
          <Checkbox
            checked={isCaseStudy === 1}
            onChange={(e) => {
              const checked = e.target.checked;
              setIsCaseStudy(checked ? 1 : 0);
              if (checked) {
                setIsDirectQuestion(0);
                setIsScenarioBased(0);
                setSelectedDirectAttachment(null);
  setDirectAttachmentType(null);
mediaUrl=''
  setMediaUrl1('');
                setQuestionType('Case Study')
              } else {
                setCaseStudyOption('');
                setSelectedPdfDetails(null);
              }
            }}
            />
        }
        label={
          <Box sx={questionTypeLabelSx}>
            <Typography component="span" sx={questionTypeTitleSx}>Case Study Questions</Typography>
            <Typography component="span" sx={questionTypeHintSx}>One passage or media with multiple linked items</Typography>
          </Box>
        }
      />

      {isDirectQuestion === 1 && (
  <Box sx={directAttachmentPanelSx}>
    <Typography
      sx={{
        color: 'text.secondary',
        fontWeight: 700,
        fontSize: '0.72rem',
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
      }}
    >
      Direct question attachment
    </Typography>
    <Box sx={directAttachmentDropzoneSx(Boolean(selectedDirectAttachment))}>
      <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', fontWeight: 500, flex: 1, minWidth: 180 }}>
        {selectedDirectAttachment
          ? 'Source file attached — questions will reference this material.'
          : 'Optional PDF or image to ground direct questions in course content.'}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'wrap',
          justifyContent: 'flex-end',
        }}
      >
    <Button
      variant="outlined"
      size="small"
      onClick={() => {
        setUseDirectAttachment(true);
        setOpen1(true);
      }}
      sx={quickActionButtonSx}
    >
      {selectedDirectAttachment ? 'Change Attachment' : 'Use Attachment'}
    </Button>

    {selectedDirectAttachment && (
  <Chip
    label={selectedDirectAttachment.name}
    onDelete={() => {
      setSelectedDirectAttachment(null);
      setDirectAttachmentType(null);
      setPassage('')
      mediaUrl=''
      setMediaUrl1('');
    }}
    variant="outlined"
    sx={{
      borderRadius: '999px',
      maxWidth: { xs: '100%', sm: 320 },
      fontWeight: 600,
      borderColor: PORTAL_ACCENT,
      color: 'text.primary',
      backgroundColor: PORTAL_ACCENT_SOFT,
      '& .MuiChip-label': {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      },
    }}
  />
)}
    </Box>
    </Box>
  </Box>
)}

       {isCaseStudy === 1 && (
  <Box sx={caseStudyPanelSx}>
    <FormControl component="fieldset" fullWidth>
      <RadioGroup
        value={caseStudyOption}
        sx={{ gap: 1 }}
        onChange={(e) => {
          const value = e.target.value;
          setCaseStudyOption(value);
          setSelectedDirectAttachment(null);
setDirectAttachmentType(null);
mediaUrl=''
setMediaUrl1('');

          // Reset both inputs when changing
          setGenerateUsingGradeLevel(false);
          setCaseStudyTitleOrGradle('');

          if (value === 'attachment') {
            handleGenerateClick(); // Trigger PDF selection
          }

          if (value === 'generate') {
            setSelectedPdfDetails(null);
          }

          if (value === 'generateImage' || value === 'generateAudio' || value === 'generateVideo') {
            setSelectedPdfDetails(null);
          }
        }}
      >
        {/* Use Attachment Option */}
        <FormControlLabel
          value="attachment"
          sx={optionRowSx(caseStudyOption === 'attachment')}
          control={
            <Radio
            />
          }
          label={
            <Box display="flex" alignItems="center" gap={1.25} flexWrap="wrap">
              <Typography variant="body1" fontWeight={700} sx={{ minWidth: 150, fontSize: '0.85rem' }}>
                Use Attachment
              </Typography>

              {/* Chip to show PDF */}
              {selectedPdfDetails && (
                <Chip
                  label={selectedPdfDetails.name}
                  onDelete={() => {
                    setCaseStudyOption('generate');
                    setSelectedPdfDetails(null);
                    setSelectedPdf(null);
                  }}
                  variant="outlined"
                  sx={{
                    borderRadius: '999px',
                    maxWidth: { xs: '100%', sm: 280 },
                    fontWeight: 600,
                    borderColor: PORTAL_ACCENT,
                    backgroundColor: PORTAL_ACCENT_SOFT,
                    '& .MuiChip-label': {
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    },
                  }}
                  deleteIcon={
                    <Tooltip title="Discard PDF">
                      <ClearIcon />
                    </Tooltip>
                  }
                />
              )}
              {/* Checkbox for grade level */}
              {caseStudyOption === 'attachment' && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={generateUsingGradeLevel}
                      onChange={(e) => {setGenerateUsingGradeLevel(e.target.checked);setCaseStudyTitleOrGradle(gradeLevel)}}
                      sx={{ padding: '5px',marginLeft:'10px' }}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ fontSize: '0.78rem', fontWeight: 700 }}>Generate using grade level</Typography>
                  }
                />
              )}
            </Box>
          }
        />

        {/* Generate Passage Option */}
        <FormControlLabel
          value="generate"
          sx={optionRowSx(caseStudyOption === 'generate')}
          control={
            <Radio
            />
          }
          label={
            <Box display="flex" alignItems="center" gap={1.25} flexWrap="wrap">
              <Typography variant="body1" fontWeight={700} sx={{ minWidth: 150, fontSize: '0.85rem' }}>
                Generate Passage
              </Typography>

              {/* Text field for title */}
              {caseStudyOption === 'generate' && (
                <TextField
                  size="small"
                  label="Title/Summary"
                  value={caseStudyTitleOrGradle}
                  onChange={(e) => setCaseStudyTitleOrGradle(e.target.value)}
                  sx={{
                    width: { xs: '100%', sm: 230 },
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '10px',
                      backgroundColor: '#fff',
                    },
                  }}
                />
              )}
              {caseStudyOption === 'generate' && renderCaseStudyEditPromptButton()}
            </Box>
          }
        />

        {/* Generate Image Option */}
        <FormControlLabel
          value="generateImage"
          sx={optionRowSx(caseStudyOption === 'generateImage')}
          control={
            <Radio
            />
          }
          label={
            <Box display="flex" alignItems="center" gap={1.25} flexWrap="wrap">
              <Typography variant="body1" fontWeight={700} sx={{ minWidth: 150, fontSize: '0.85rem' }}>
                Generate Image
              </Typography>
              {caseStudyOption === 'generateImage' && renderCaseStudyEditPromptButton()}
            </Box>
          }
        />

        {/* Subject + hazard mode drive the image prompt's SUBJECT row and
            HAZARD_MODE flag. Everything else in its FIELDS block is taken from the
            assessment automatically. */}
        {caseStudyOption === 'generateImage' && (
          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap" sx={{ pl: 4, pb: 1 }}>
            <FormControl size="small" sx={{ minWidth: 190 }}>
              <InputLabel id="case-study-subject-label">Subject</InputLabel>
              <Select
                labelId="case-study-subject-label"
                label="Subject"
                value={caseStudySubject}
                onChange={(e) => setCaseStudySubject(e.target.value)}
              >
                <MenuItem value="MATH">Math</MenuItem>
                <MenuItem value="ELA">English language arts</MenuItem>
                <MenuItem value="SCIENCE">Science</MenuItem>
                <MenuItem value="SOCIAL">Social studies</MenuItem>
                <MenuItem value="CTE">Career &amp; technical</MenuItem>
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Checkbox
                  checked={caseStudyHazardMode}
                  onChange={(e) => setCaseStudyHazardMode(e.target.checked)}
                />
              }
              label={
                <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                  Include one safety violation
                </Typography>
              }
            />
          </Box>
        )}

        {/* Generate Audio Option */}
        <FormControlLabel
          value="generateAudio"
          sx={optionRowSx(false, true)}
          control={
            <Radio
              disabled
            />
          }
          label={
            <Typography variant="body1" fontWeight={700} sx={{ fontSize: '0.85rem' }}>
              Generate Audio <span style={{ fontStyle: 'italic' }}>(Coming soon)</span>
            </Typography>
          }
        />

        {/* Generate Video Option */}
        <FormControlLabel
          value="generateVideo"
          sx={optionRowSx(false, true)}
          control={
            <Radio
              disabled
            />
          }
          label={
            <Typography variant="body1" fontWeight={700} sx={{ fontSize: '0.85rem' }}>
              Generate Video <span style={{ fontStyle: 'italic' }}>(Coming soon)</span>
            </Typography>
          }
        />
      </RadioGroup>
    </FormControl>
  </Box>
)}

      {(userType === '2' || isCaseStudy === 1) && (
        <Box sx={promptToolbarSx}>
          {userType === '2' && (
            <>
              <Button
                onClick={() => showGeneratePrompt(data)}
                variant="outlined"
                color="primary"
                size="small"
                disabled={loadingShowPrompt}
                startIcon={!loadingShowPrompt && <AutoAwesomeIcon sx={{ fontSize: 16 }} />}
                sx={quickActionButtonSx}
              >
                {loadingShowPrompt ? (
                  <ButtonSpinnerSlot size={16} />
                ) : (
                  'Show Prompt'
                )}
              </Button>
              <Button
                disabled={loadingEditPrompt}
                onClick={() => { EditPrompt(); }}
                variant="outlined"
                color="primary"
                size="small"
                startIcon={!loadingEditPrompt && <EditNoteIcon sx={{ fontSize: 16 }} />}
                sx={quickActionButtonSx}
              >
                {loadingEditPrompt ? (
                  <ButtonSpinnerSlot size={16} />
                ) : (
                  'Edit Prompt'
                )}
              </Button>
            </>
          )}

          {isCaseStudy === 1 && (
            <>
              <Button
                onClick={() => handleGenerateCaseStudy()}
                variant="contained"
                color="primary"
                size="small"
                disabled={loadingGenerate}
                startIcon={!loadingGenerate && <AutoAwesomeIcon sx={{ fontSize: 16 }} />}
                sx={{ ...quickActionButtonSx, minWidth: 128 }}
              >
                {loadingGenerate ? (
                  <ButtonSpinnerSlot size={16} />
                ) : (
                  passage ? 'Regenerate' : 'Generate'
                )}
              </Button>

              {passage && (
                <Button
                  onClick={handleShowCaseStudy}
                  variant="outlined"
                  color="primary"
                  size="small"
                  sx={{ ...quickActionButtonSx, minWidth: 98 }}
                >
                  Show
                </Button>
              )}
            </>
          )}
        </Box>
      )}
    </Box>
  </FormControl>

  <Container>
  

     <Dialog
  open={open1}
  onClose={handleDialogClose}
  maxWidth="sm"
  fullWidth
>
  <DialogTitle>Select an Attachment</DialogTitle>

  <DialogContent dividers>
    {pdfUrls.length > 0 ? (
     <RadioGroup
  value={selectedPdf}
  onChange={(e) => handlePdfSelect(e.target.value)}
>
  {pdfUrls.map((pdf, index) => (
    <Paper
      key={pdf.pdfId}
      variant="outlined"
      sx={{
        mb: 1.5,
        px: 2,
        py: 1.5,
        borderRadius: '10px',
        borderColor: selectedPdf === pdf.pdfId ? PORTAL_ACCENT : 'divider',
        backgroundColor: selectedPdf === pdf.pdfId ? PORTAL_ACCENT_SOFT : 'background.paper',
        cursor: "pointer",
        transition: "border-color 0.2s ease, background 0.2s ease",
        "&:hover": { borderColor: PORTAL_ACCENT },
      }}
    >
      <FormControlLabel
        value={pdf.pdfId}
        control={<Radio />}
        label={
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {index + 1}) {pdf.name} ({getFileType(pdf.pdfUrl)})
          </Typography>
        }
        sx={{ width: "100%", margin: 0 }}
      />
    </Paper>
  ))}
</RadioGroup>
    ) : (
      <Typography color="text.secondary">
        No PDFs available.
      </Typography>
    )}
  </DialogContent>

  <DialogActions>
    <Button
      variant="outlined"
      color="error"
      onClick={() => {
        setCaseStudyOption("generate");
        handleDialogClose();
      }}
      sx={quickActionButtonSx}
    >
      Cancel
    </Button>

    <Button
      variant="contained"
      onClick={handleOkClick}
      disabled={!selectedPdf}
      sx={quickActionButtonSx}
    >
      OK
    </Button>
  </DialogActions>
</Dialog>
      
    </Container>
  </Box>
</Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 0, m: 0 }}>
                  <ModalFoot>
                    <Btn
                      type="button"
                      $variant="ghost"
                      disabled={loading}
                      onClick={() => {
                    setExamType('');
    setNoOfQuestions('');
    setGradeLevel('');
    setLanguage('English');
    setAssessmentLevel(['Analyze']);
    setIsScenarioBased(0);
    setIsCaseStudy(0);
    setIsDirectQuestion(1);
    setCaseStudyOption('generate');
    setPassage('');
    setQuestions('');
    setSelectedPdfDetails(null);
    setSelectedPdf(null);
    setGenerateUsingGradeLevel(false);
    setCaseStudyTitleOrGradle('');
    setSelectedDirectAttachment(null);
    setDirectAttachmentType(null);
    setSelectedDirectAttachment(null);
    setEditablePrompt('')
    setCaseStudyEditablePrompt('');
    setIsCaseStudyPromptDialogOpen(false);
  
mediaUrl=''
setMediaUrl1('');

    // Reset domains
    setSelectedDomains(originalDomainData.map(d => d.name));
    const allSubs = originalDomainData.flatMap(d =>
  d.subDomain?.map(s => s.name) || []
    );
    setSelectedSubdomains(allSubs);
    setDomainData(originalDomainData);
    handleClose()}}
                    >
                      Close
                    </Btn>
                    <ModalFootActions>
                      {renderQuestions()}
                      <Btn
                        type="button"
                        $variant="subtle"
                        disabled={loading || loadingShowPrompt}
                        onClick={() => handleGenerateQuestions(false)}
                        style={{ minWidth: 168 }}
                      >
                        Generate Questions
                      </Btn>
                    </ModalFootActions>
                  </ModalFoot>
                </DialogActions>
            </Dialog>
            {alertMessage && (
                <Box
                    position="fixed"
                    top="42%"
                    left="32%"
                    transform="translate(-50%, -50%)"
                    bgcolor="oklch(1 0 0)"
                    border="1px solid oklch(0.915 0.006 260)"
                    borderRadius="16px"
                    boxShadow="0 24px 50px -16px rgba(17,20,38,.30)"
                    p={4}
                    zIndex={9999}
                    textAlign="center"
                >
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700 }}>{alertMessage}</Typography>
                    <Button onClick={() => setAlertMessage('')} variant="contained" color="primary">Close</Button>
                </Box>
            )}
            {generatingQuestions && (
    <LoadingPopup
        domainStatus={domainStatus}
        onCancel={() => { setGeneratingQuestions(false); setDomainStatus([]); }}
    />
)}
            <DemoLockedDialog open={lockedDialogOpen} onClose={() => setLockedDialogOpen(false)} />
        </>
    );
}
